import type { Project, UserOrganization } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Building2, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Sparkline } from '@/components/sparkline';
import { EmptyState, ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { compactNumber } from '@/lib/format';
import { allows, useOrganizations } from '@/lib/roles';
import { useTeams } from '@/lib/teams';
import { cn } from '@/lib/utils';

export function ProjectsPage() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);

  const projects = useQuery({ queryKey: ['projects'], queryFn: () => client.listProjects() });
  const organizations = useOrganizations();
  const organization = organizations.data?.[0];
  const canCreate = allows(organization?.role, 'admin');

  const createProject = useMutation({
    mutationFn: async (projectName: string) => {
      if (!organization) throw new Error('No organization available to create a project in');
      return client.createProject(organization.id, projectName);
    },
    onSuccess: () => {
      setOpen(false);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Projects</h1>
          <p className="text-sm text-muted-foreground">
            Each project has its own DSN, issues and alert rules.
          </p>
        </div>

        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus />
                New project
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a project</DialogTitle>
                <DialogDescription>
                  A DSN key is generated automatically so you can start reporting immediately.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label htmlFor="project-name">Name</Label>
                <Input
                  id="project-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="checkout-web"
                />
              </div>
              {createProject.error && (
                <p className="text-sm text-destructive">{(createProject.error as Error).message}</p>
              )}
              <DialogFooter>
                <Button
                  onClick={() => createProject.mutate(name)}
                  disabled={!name.trim() || createProject.isPending}
                >
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {(projects.isLoading || organizations.isLoading) && <ListSkeleton rows={3} />}
      {projects.isError && (
        <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
      )}
      {organizations.isError && (
        <ErrorState error={organizations.error} onRetry={() => void organizations.refetch()} />
      )}

      {projects.data &&
        organizations.data?.map((org) => (
          <OrganizationProjects
            key={org.id}
            organization={org}
            projects={projects.data.filter((project) => project.org_id === org.id)}
          />
        ))}
    </div>
  );
}

/**
 * One organization's projects. Shown even with no projects, so the organization
 * is visible before anything has been created in it.
 */
function OrganizationProjects({
  organization,
  projects,
}: {
  organization: UserOrganization;
  projects: Project[];
}) {
  const active = projects.filter((project) => project.is_active);
  const archived = projects.filter((project) => !project.is_active);
  const teams = useTeams(organization.id);
  const teamName = (project: Project) =>
    teams.data?.find((team) => team.id === project.team_id)?.name;

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Building2 className="size-4 text-muted-foreground" />
        <h2 className="min-w-0 truncate text-sm font-medium">{organization.name}</h2>
      </div>

      {projects.length === 0 &&
        (allows(organization.role, 'admin') ? (
          <EmptyState
            icon={<Boxes className="size-5" />}
            title="No projects yet"
            description="Create your first project to get a DSN and start receiving errors."
          />
        ) : (
          <EmptyState
            icon={<Boxes className="size-5" />}
            title="No projects yet"
            description="You haven't been added to any projects. Ask an organization admin to add you."
          />
        ))}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {active.map((project) => (
          <ProjectCard key={project.id} project={project} team={teamName(project)} />
        ))}
      </div>

      {archived.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">Archived</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {archived.map((project) => (
              <ProjectCard key={project.id} project={project} team={teamName(project)} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function ProjectCard({ project, team }: { project: Project; team?: string }) {
  const { client } = useAuth();

  // Loaded per card, like the issue sparklines, so the list never waits on it.
  const stats = useQuery({
    queryKey: ['project-stats', project.id],
    queryFn: () => client.projectStats(project.id, 24),
    staleTime: 60_000,
  });
  const total = useQuery({
    queryKey: ['project-total', project.id],
    queryFn: () => client.projectTotal(project.id),
    staleTime: 60_000,
  });

  return (
    <Link to={`/projects/${project.id}/issues`}>
      <Card
        className={cn(
          'h-full transition-colors hover:border-input-hover hover:shadow-sm',
          !project.is_active && 'opacity-70',
        )}
      >
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0 pb-2">
          <CardTitle className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-base">
            <span className="wrap-anywhere">{project.name}</span>
            {!project.is_active && <Badge variant="muted">archived</Badge>}
            {/* Which team looks after it, since that is often why it is here. */}
            {team && <Badge variant="outline">{team}</Badge>}
          </CardTitle>
          {total.data && (
            <span
              className="shrink-0 text-sm tabular-nums text-muted-foreground"
              title={`${total.data.events.toLocaleString()} events in total`}
            >
              {compactNumber(total.data.events)} {total.data.events === 1 ? 'event' : 'events'}
            </span>
          )}
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          {project.platforms.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {project.platforms.map((platform) => (
                <Badge key={platform} variant="secondary">
                  {platform}
                </Badge>
              ))}
            </div>
          ) : (
            <p>No platform detected yet</p>
          )}
          <Sparkline buckets={stats.data ?? []} width={300} height={40} className="w-full" />
        </CardContent>
      </Card>
    </Link>
  );
}
